<script lang="ts">
	import RoundArrowForward from 'virtual:icons/ic/round-arrow-forward';
	import { PUBLIC_API_URL } from '$env/static/public';
	import { slugify } from '@lib/helpers/global';
	import { toast } from 'svelte-sonner';
	import { writable, type Writable } from 'svelte/store';
	import LayoutGrid from 'virtual:icons/lucide/layout-grid';
	import Spinner from '../global/micro/Spinner.svelte';
	import BookCard from '../global/BookCard.svelte';
	import { getBundles, getCategories, searchBooks } from '@lib/utils';
	import { createFloatingActions } from 'svelte-floating-ui';
	import { offset, shift, flip } from 'svelte-floating-ui/core';
	import { fade, fly } from 'svelte/transition';
	import { clickoutside } from '@svelte-put/clickoutside';
	import { bookMenuItems } from '@lib/data/main-menu-items';
	import BundleCard from './home/BundleCard.svelte';
	import { afterNavigate } from '$app/navigation';

	const activeIdentifier: Writable<(typeof bookMenuItems)[number]['identifier']> = writable(
		bookMenuItems[0].identifier
	);

	const BOOK_GRID_LIMIT = 9;
	const BUNDLE_GRID_LIMIT = 5;
	const CATEGORIES_LIMIT = 51;

	async function getBooks(params?: Record<string, string>) {
		const apiUrl = new URL(`${PUBLIC_API_URL}/book-digital`);
		const books = await searchBooks(apiUrl, {
			alternateParams: {
				limit: String(BOOK_GRID_LIMIT),
				...params
			}
		})
			.then((response) => {
				if (!response) return [];

				const { data } = response;
				return data;
			})
			.catch(() => {
				toast.error(`Oops, terjadi kesalahan saat memuat buku`);
				return [];
			});

		return books;
	}

	const [floatingRef, floatingContent] = createFloatingActions({
		strategy: 'absolute',
		placement: 'bottom-end',
		middleware: [flip(), offset(12), shift({ crossAxis: true, padding: 20 })]
	});

	const isOpenMenu = writable(false);

	afterNavigate(({ to }) => {
		if (to) $isOpenMenu = false;
	});
</script>

<div class="relative" use:clickoutside on:clickoutside={() => ($isOpenMenu = false)}>
	<button
		on:click={() => ($isOpenMenu = !$isOpenMenu)}
		use:floatingRef
		type="button"
		class="btn btn-secondary no-animation btn-sm flex-1 rounded-full"
		class:btn-outline={!$isOpenMenu}
	>
		<LayoutGrid class="text-base" />
		Menu
	</button>
	{#if $isOpenMenu}
		<div
			use:floatingContent
			transition:fly={{ y: -15, duration: 350 }}
			class="menu-panel absolute z-[101] rounded-xl bg-base-100 p-7 shadow-soft pre-sm:w-[35rem] pre-lg:w-[50rem]"
		>
			<div class="flex items-start gap-16">
				<nav class="w-full max-w-32">
					<ul class="grid gap-5">
						{#each bookMenuItems as item}
							<li>
								<button
									on:click={() => ($activeIdentifier = item.identifier)}
									type="button"
									class="text-left font-semibold hover:text-secondary"
									class:text-secondary={$activeIdentifier === item.identifier}>{item.label}</button
								>
							</li>
						{/each}
					</ul>
				</nav>
				<div class="h-96 flex-1 overflow-y-auto">
					{#if $activeIdentifier === 'category'}
						{#await getCategories({ limit: String(CATEGORIES_LIMIT) })}
							<div class="grid min-h-96 place-content-center gap-3 text-center">
								<Spinner class="mx-auto" />
								<p class="text-balance text-sm font-medium">Sedang Memuat</p>
							</div>
						{:then categories}
							<div class="grid grid-cols-2 gap-3 pre-lg:grid-cols-3">
								{#each categories as category}
									<a
										href="/categories/{category.slug || slugify(category.name)}"
										class="max-w-fit text-sm font-medium hover:text-secondary">{category.name}</a
									>
								{/each}
							</div>
							{#if categories.length > CATEGORIES_LIMIT - 1}
								<a href="/search" class="mt-5 flex items-center gap-1 font-medium text-blue-500">
									Lihat Semua
									<RoundArrowForward class="text-lg" />
								</a>
							{/if}
						{/await}
					{/if}
					{#if $activeIdentifier === 'all-books'}
						{#await getBooks({ sort_by: 'newest', limit: String(BOOK_GRID_LIMIT) })}
							<div class="grid min-h-96 place-content-center gap-3 text-center">
								<Spinner class="mx-auto" />
								<p class="text-sm font-medium">Sedang Memuat</p>
							</div>
						{:then books}
							<div class="books-grid">
								{#each books.slice(0, BOOK_GRID_LIMIT - 1) as book}
									<BookCard {book} />
								{/each}
							</div>
							{#if books.length > BOOK_GRID_LIMIT - 1}
								<a href="/search" class="mt-5 flex items-center gap-1 font-medium text-blue-500">
									Lihat Semua
									<RoundArrowForward class="text-lg" />
								</a>
							{/if}
						{/await}
					{/if}
					{#if $activeIdentifier === 'free-books'}
						{#await getBooks( { is_free: String(true), sort_by: 'newest', limit: String(BOOK_GRID_LIMIT) } )}
							<div class="grid min-h-96 place-content-center gap-3 text-center">
								<Spinner class="mx-auto" />
								<p class="text-sm font-medium">Sedang Memuat</p>
							</div>
						{:then books}
							<div class="books-grid">
								{#each books.slice(0, BOOK_GRID_LIMIT - 1) as book}
									<BookCard {book} />
								{/each}
							</div>
							{#if books.length > BOOK_GRID_LIMIT - 1}
								<a
									href="/search?is_free=true"
									class="mt-5 flex items-center gap-1 font-medium text-blue-500"
								>
									Lihat Semua
									<RoundArrowForward class="text-lg" />
								</a>
							{/if}
						{/await}
					{/if}
					{#if $activeIdentifier === 'coming-soon'}
						{#await getBooks( { is_published: String(false), sort_by: 'newest_created', limit: String(BOOK_GRID_LIMIT) } )}
							<div class="grid min-h-96 place-content-center gap-3 text-center">
								<Spinner class="mx-auto" />
								<p class="text-sm font-medium">Sedang Memuat</p>
							</div>
						{:then books}
							{#if books.length}
								<div class="books-grid">
									{#each books.slice(0, BOOK_GRID_LIMIT - 1) as book}
										<BookCard {book} />
									{/each}
								</div>
							{:else}
								<p class="text-sm font-medium">Tidak ada buku tersedia...</p>
							{/if}
							{#if books.length > BOOK_GRID_LIMIT - 1}
								<a
									href="/search?is_published=false"
									class="mt-5 flex items-center gap-1 font-medium text-blue-500"
								>
									Lihat Semua
									<RoundArrowForward class="text-lg" />
								</a>
							{/if}
						{/await}
					{/if}
					{#if $activeIdentifier === 'bundles'}
						{#await getBundles( { limit: String(BUNDLE_GRID_LIMIT), sort_by: 'newest_created' } ).then( (response) => {
								if (!response) return [];
								const { data } = response;

								return data;
							} )}
							<div class="grid min-h-96 place-content-center gap-3 text-center">
								<Spinner class="mx-auto" />
								<p class="text-sm font-medium">Sedang Memuat</p>
							</div>
						{:then bundles}
							{#if bundles.length}
								<div class="bundles-grid">
									{#each bundles.slice(0, BUNDLE_GRID_LIMIT - 1) as bundle}
										<BundleCard {bundle} />
									{/each}
								</div>
							{:else}
								<p class="text-sm font-medium">Tidak ada bundle tersedia...</p>
							{/if}
							{#if bundles.length > BUNDLE_GRID_LIMIT - 1}
								<a href="/bundles" class="mt-5 flex items-center gap-1 font-medium text-blue-500">
									Lihat Semua
									<RoundArrowForward class="text-lg" />
								</a>
							{/if}
						{/await}
					{/if}
				</div>
			</div>
		</div>
	{/if}
</div>

{#if $isOpenMenu}
	<div
		transition:fade={{ duration: 350 }}
		class="overlay fixed left-0 top-0 z-[100] size-full bg-black/30"
	></div>
{/if}

<style lang="postcss">
	.books-grid {
		@apply grid grid-cols-2 gap-3 pre-lg:grid-cols-4;
	}

	.bundles-grid {
		@apply grid grid-cols-2 gap-3;
	}
</style>
